import { useCallback, useEffect, useState } from "react";
import { Card, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import { Icon } from "../../components";
import { toast } from "react-toastify";
import Pagination from "../Constant/Pagination";
import DebounceSearchInput from "../Constant/DebounceSearchInput";
import { CancelRequest } from "../../services/AxiosInstance";
import AddVendor from "./AddVendor";
import { getVendorList, importVendors } from "../../services/VendorServices";
import BarLoader from "../../components/Loader/BarLoader";
import EditVendor from "./EditVendor";
import FileUpload from "../Constant/fileupload";

function ExportConfirmModal({ show, onClose, onConfirm }) {
  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>Are you sure you want to export the vendor list?</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="success" onClick={onConfirm}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function ImportSummaryModal({ show, onClose, summary }) {
  if (!summary) return null;

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable="true">
      <Modal.Header closeButton>
        <Modal.Title>Import Summary</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          <strong>Vendors Created:</strong> {summary.created}
        </p>
        {summary.skipped?.length > 0 && (
          <>
            <p>
              <strong>Skipped Records:</strong>
            </p>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {summary.skipped.map(([row, reason], idx) => (
                  <tr key={idx}>
                    <td>{row}</td>
                    <td>{reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function ImportVendorModal({ show, onClose, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.warning("Please select a file.");
      return;
    }
    try {
      setUploading(true);
      const response = await importVendors(selectedFile);
      const { message, results } = response.data;
      toast.success(message);
      setImportResult(results);
      setShowSummary(true);
      onUploaded();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Import failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import Vendors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mt-2">
            <FileUpload
              label="Drag & drop File (max size 3MB)"
              accept=".xlsx,.xls,.csv"
              multiple={false}
              value={selectedFile}
              onChange={(file) => setSelectedFile(file)}
            />
            <div className="col-12 mt-2">
              <code className="text-light small d-block">
                <strong>Note:</strong> Upload an Excel file (.xlsx/.xls/.csv)
                with valid Vendor details.
              </code>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ImportSummaryModal
        show={showSummary}
        onClose={() => setShowSummary(false)}
        summary={importResult}
      />
    </>
  );
}

function VendorListPage() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showItemPerPage, setShowItemPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ query: "", is_active: "" });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getVendorList(
        currentPage,
        showItemPerPage,
        filter
      );
      setData(response.data.results);
      setTotal(response.data.count);
    } catch (error) {
      toast.error(error.response?.data?.message);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, showItemPerPage, filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePagination = (page) => {
    setCurrentPage(page.selected + 1);
  };

  const handleSearchChange = (value) => {
    if (value.length > 0) CancelRequest();
    setFilter((prev) => ({ ...prev, query: value }));
    setCurrentPage(1);
  };

  const handleOnVendorAdded = (newUser) => {
    setData((prev) => [newUser, ...prev]);
    setTotal((prevTotal) => prevTotal + 1);
    setShowModal(false);
  };

  const handleExportConfirmed = async () => {
    try {
      setShowExportModal(false);
      const response = await getVendorList(1, 10000, filter, true);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "vendors.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Vendors exported successfully!");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <div>
      <Block.Head className="mb-2">
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Vendors</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Vendors
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex">
              <li>
                <Button
                  className="d-md-none"
                  size="md"
                  variant="primary"
                  onClick={() => setShowModal(true)}
                >
                  <Icon name="plus" />
                  <span>Add</span>
                </Button>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="outline-primary"
                  onClick={() => setShowModal(true)}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="plus" />
                  <span>Add Vendor</span>
                </Button>
              </li>

              <li>
                <Button
                  className="d-none d-md-inline-flex ms-2 py-1 px-2"
                  variant="outline-secondary"
                  onClick={() => setShowImportModal(true)}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="upload" />
                  <span className="ms-1">Import</span>
                </Button>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex ms-2 py-1 px-2"
                  variant="outline-secondary"
                  onClick={() => setShowExportModal(true)}
                  style={{ fontSize: "0.875rem", height: "35px" }}
                >
                  <Icon name="download" />
                  <span className="ms-1">Export</span>
                </Button>
              </li>
            </ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card>
          <div className="data-table-wrapper d-flex align-items-center justify-content-start p-3">
            <div>
              <DebounceSearchInput handleSearchChange={handleSearchChange} />
            </div>
          </div>

          <div className="table-responsive">
            {isLoading ? (
              <div className="text-center my-4">
                <BarLoader color="#255D5C" size={50} />
              </div>
            ) : (
              <>
                <table className="table  border-less fs-6">
                  <thead className="table-light custom-thead">
                    <tr>
                      <th>Vendor Name</th>
                      <th>Contact Person</th>
                      <th>Email</th>
                      <th>Mobile Number</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No vendors found
                        </td>
                      </tr>
                    ) : (
                      data.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <strong>
                              <Link
                                to={`/vendor/${row.id}`}
                                state={{ vendor: row }}
                              >
                                {row.name}
                              </Link>
                            </strong>
                          </td>
                          <td>{row.contact_person_name}</td>
                          <td>{row.email}</td>
                          <td>{row.mobile_number}</td>
                          <td>{row.address}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <div className="p-3">
                  <Pagination
                    currentPage={currentPage}
                    recordsPage={showItemPerPage}
                    dataLength={total}
                    handlePagination={handlePagination}
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      </Block>

      {/* Modals */}
      <AddVendor
        showModal={showModal}
        setShowModal={setShowModal}
        onVendorAdded={handleOnVendorAdded}
      />
      <EditVendor
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        vendorData={selectedVendor}
        onVendorUpdated={() => {
          fetchData();
          setShowEditModal(false);
        }}
      />
      <ExportConfirmModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirmed}
      />
      <ImportVendorModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onUploaded={fetchData}
      />
    </div>
  );
}

export default VendorListPage;
